import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaskIcon } from '../components/Icon';
import { useBranding } from '@alterrego/module-branding';
import { apiFetch } from '../utils/api';
import './DiscuterScreen.css';

// ── Types alignés sur la réponse backend /reservations/chat ────────────────
interface ChatAction {
  label: string;
  action?: 'open_reserver';
  value?: string;
  prefill?: { date?: string; heure?: string; couverts?: number };
}
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  actions?: ChatAction[];
  error?: boolean;
}

const MAX_HISTORY = 40;          // tours envoyés au backend (stateless)
const ANON_SOFT_LIMIT = 10;      // au-delà → mur de connexion (aligné backend)
const REQUEST_TIMEOUT_MS = 30000;
const MAXLEN = 2000;

const WELCOME =
  "Bonjour, je suis Maïa. Je peux vous aider à réserver une table, vous renseigner sur notre carte et nos horaires, ou transmettre un message à l'équipe. Que puis-je faire pour vous ?";

const QUICK_CHIPS: { label: string; seed: string }[] = [
  { label: 'Réserver une table', seed: 'Je voudrais réserver une table.' },
  { label: 'Voir la carte', seed: 'Pouvez-vous me parler de votre carte ?' },
  { label: 'Laisser un message', seed: "Je voudrais laisser un message à l'équipe." },
];

// Rendu léger : **gras** → <strong>, sans dangerouslySetInnerHTML (anti-XSS).
// Les retours à la ligne sont gérés par white-space: pre-wrap.
function renderRich(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') && p.length > 4
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>,
  );
}

// Avatar Maïa (illustration importée de myPlace). Fallback initiale « M » sur
// pastille si l'image ne charge pas.
function MaiaAvatar({ size, className }: { size: number; className?: string }) {
  const [failed, setFailed] = useState(false);
  const style = { width: size, height: size } as React.CSSProperties;
  if (failed) {
    return (
      <span className={`dsc-avatar ${className ?? ''}`} style={{ ...style, fontSize: Math.round(size * 0.42) }} aria-hidden>M</span>
    );
  }
  return (
    <img
      src="/avatars/maia.png"
      alt="Maïa"
      className={`dsc-avatar-img ${className ?? ''}`}
      style={style}
      onError={() => setFailed(true)}
    />
  );
}

function getJwt(): string | null {
  try { return localStorage.getItem('alterrego_jwt'); } catch { return null; }
}
function isLogged(): boolean {
  return !!getJwt();
}

export function DiscuterScreen() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loginWall, setLoginWall] = useState(false);
  const [kbInset, setKbInset] = useState(0);

  const listRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  // Nombre de messages envoyés en anonyme cette session (mur de connexion).
  const anonSentRef = useRef(0);

  const started = messages.length > 0;

  const scrollToBottom = useCallback((smooth = true) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // Mount : aller en bas immédiatement (utile si on revient avec un historique).
  useEffect(() => { scrollToBottom(false); }, [scrollToBottom]);
  // Nouveau message (user OU Maïa) + bascule typing → garder le dernier visible.
  useEffect(() => { scrollToBottom(); }, [messages.length, busy, scrollToBottom]);

  // Clavier iOS : remonte la barre d'envoi au-dessus du clavier (visualViewport)
  // ET re-scrolle en bas (après ~100ms, le temps que le clavier monte) pour que
  // la dernière bulle reste visible.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => {
      const delta = window.innerHeight - vv.height - (vv.offsetTop || 0);
      setKbInset(delta > 80 ? delta : 0);
      setTimeout(() => scrollToBottom(), 100);
    };
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, [scrollToBottom]);

  const autoResize = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
  }, []);

  // Un POST avec timeout + 1 retry réseau. Renvoie la réponse JSON ou throw.
  const postChat = useCallback(async (history: ChatMessage[]): Promise<any> => {
    const body = JSON.stringify({
      messages: history.slice(-MAX_HISTORY).map((m) => ({ role: m.role, content: m.content })),
    });
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const attempt = async (): Promise<Response> => {
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
      try {
        return await apiFetch(`/reservations/chat`, {
          method: 'POST', headers, body, signal: ctrl.signal,
        });
      } finally {
        clearTimeout(to);
      }
    };

    let res: Response;
    try {
      res = await attempt();
    } catch {
      // Retry réseau unique.
      res = await attempt();
    }
    const data = await res.json().catch(() => ({ error: 'server' }));
    return { status: res.status, data };
  }, []);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim().slice(0, MAXLEN);
    if (!trimmed || busy || loginWall) return;

    // Mur de connexion proactif (fair use anonyme).
    if (!isLogged() && anonSentRef.current >= ANON_SOFT_LIMIT) {
      setLoginWall(true);
      return;
    }

    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    if (taRef.current) taRef.current.style.height = 'auto';
    setBusy(true);
    if (!isLogged()) anonSentRef.current += 1;

    try {
      const { status, data } = await postChat(history);
      if (status === 429 || data?.error === 'limit') {
        setLoginWall(true);
        setBusy(false);
        return;
      }
      if (!data || data.error) {
        const msg = data?.message || "Désolé, je ne peux pas répondre pour le moment. Réessayez.";
        setMessages((m) => [...m, { role: 'assistant', content: msg, error: true }]);
      } else {
        setMessages((m) => [...m, {
          role: 'assistant',
          content: data.reply || '…',
          actions: Array.isArray(data.actions) ? data.actions : undefined,
        }]);
      }
    } catch {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: 'La connexion est lente ou indisponible. Réessayez dans un instant.',
        error: true,
      }]);
    } finally {
      setBusy(false);
    }
  }, [busy, loginWall, messages, postChat]);

  const onAction = useCallback((a: ChatAction) => {
    if (a.action === 'open_reserver') {
      const p = a.prefill || {};
      const qs = new URLSearchParams();
      if (p.date) qs.set('date', p.date);
      if (p.heure) qs.set('time', p.heure);
      if (p.couverts) qs.set('guests', String(p.couverts));
      navigate(`/reserver${qs.toString() ? `?${qs}` : ''}`);
      return;
    }
    if (a.value) send(a.value);
  }, [navigate, send]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  return (
    <div className="dsc" style={kbInset ? ({ ['--dsc-kb']: `${kbInset}px` } as React.CSSProperties) : undefined}>
      <header className="dsc-header">
        <MaiaAvatar size={44} />
        <div className="dsc-header-text">
          <h1>Maïa</h1>
          <span className="dsc-header-sub">Hôtesse de {branding.displayName}</span>
        </div>
        <button
          className="dsc-header-close"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
        >
          Fermer
        </button>
      </header>

      <div className="dsc-messages" ref={listRef}>
        {/* Accueil : grand avatar centré + message de bienvenue + chips d'amorce. */}
        <div className="dsc-welcome">
          <MaiaAvatar size={88} className="dsc-welcome-avatar" />
          <div className="dsc-bubble assistant dsc-welcome-bubble">{WELCOME}</div>
        </div>
        {!started && (
          <div className="dsc-quick">
            {QUICK_CHIPS.map((c) => (
              <button key={c.label} className="dsc-chip" onClick={() => send(c.seed)}>{c.label}</button>
            ))}
          </div>
        )}

        {messages.map((m, i) => {
          // Avatar uniquement sur la 1re bulle assistant d'un bloc consécutif
          // (évite la répétition) ; sinon un espaceur préserve l'alignement.
          const showAvatar = m.role === 'assistant' && (i === 0 || messages[i - 1].role !== 'assistant');
          const needSpacer = m.role === 'assistant' && !showAvatar;
          return (
          <div key={i} className={`dsc-row ${m.role}`}>
            {showAvatar && <MaiaAvatar size={32} />}
            {needSpacer && <span className="dsc-avatar-spacer" aria-hidden />}
            <div className={`dsc-bubble ${m.role}${m.error ? ' error' : ''}`}>
              {m.role === 'assistant' ? renderRich(m.content) : m.content}
              {m.actions && m.actions.length > 0 && (
                <div className="dsc-actions">
                  {m.actions.map((a, j) => (
                    <button key={j} className={`dsc-action${a.action === 'open_reserver' ? ' primary' : ''}`} onClick={() => onAction(a)}>
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          );
        })}

        {busy && (
          <div className="dsc-row assistant">
            <MaiaAvatar size={32} />
            <div className="dsc-bubble assistant dsc-typing">
              <span className="dsc-typing-dots"><span>.</span><span>.</span><span>.</span></span>
            </div>
          </div>
        )}
      </div>

      {loginWall ? (
        <div className="dsc-wall">
          <p>Connectez-vous pour continuer la conversation avec Maïa.</p>
          <button className="dsc-wall-btn" onClick={() => navigate('/compte')}>Se connecter</button>
        </div>
      ) : (
        <div className="dsc-inputbar">
          <textarea
            ref={taRef}
            className="dsc-input"
            value={input}
            onChange={(e) => { setInput(e.target.value); autoResize(); }}
            onKeyDown={onKeyDown}
            onFocus={() => setTimeout(() => scrollToBottom(), 250)}
            placeholder="Votre message…"
            rows={1}
            maxLength={MAXLEN}
            autoComplete="off"
          />
          <button
            className="dsc-send"
            disabled={busy || !input.trim()}
            onClick={() => send(input)}
            aria-label="Envoyer"
          >
            <MaskIcon name="arrow-up-from-bracket" size={20} color="#fff" />
          </button>
        </div>
      )}
    </div>
  );
}
